import { DndContext, DragEndEvent } from "@dnd-kit/core";
import { VisibilityOutlined } from "@mui/icons-material";
import { Box, Button, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { BaseModalComponent } from "../../common/BaseModalComponent";
import { usePostMutation } from "../hooks/useSectorWorkflow.mutation";
import { useDeleteWithBodyMutation } from "../hooks/useSectorWorkflowDeleteItem.mutation";
import {
  IConditionalRelationBulkCreateItem,
  IConditionalRelationBulkCreatePayload,
  IConditionalRelationBulkDeletePayload,
  IContextSelectionMap,
  IContextSelectorDefinition,
  ISelectorOption,
  IWorkflowBaseRecord,
  IWorkflowNodeConfig,
} from "../interfaces";
import { BindingCatalogPanel } from "./BindingCatalogPanel";
import { ConditionalRelationChangesModal } from "./ConditionalRelationChangesModal";
import { ContextSelectorBar } from "./ContextSelectorBar";
import { RelationRecordList } from "./RelationRecordList";

interface IConditionalRelationChangesPayload {
  addedItems: IWorkflowBaseRecord[];
  removedItems: IWorkflowBaseRecord[];
}

interface IDragItemData {
  source: "used" | "available";
  record: IWorkflowBaseRecord;
}

interface IConditionalRelationManagerProps {
  node: IWorkflowNodeConfig;
  selectors?: IContextSelectorDefinition[];
  contextSelection: IContextSelectionMap;
  onContextChange: (key: string, value: string) => void;
  optionsMap: Record<string, ISelectorOption[]>;

  records: IWorkflowBaseRecord[];
  selectedRecordUid: string | null;
  onSelectRecord: (uid: string) => void;

  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  onLoadMore?: () => void;

  requireCompleteContextSelection?: boolean;
  onSaveChanges?: (payload: IConditionalRelationChangesPayload) => Promise<void> | void;
}

export const ConditionalRelationManager = (props: IConditionalRelationManagerProps) => {
  const {
    node,
    selectors,
    contextSelection,
    onContextChange,
    optionsMap,
    records,
    selectedRecordUid,
    onSelectRecord,
    loading = false,
    error = null,
    hasMore = false,
    isFetchingMore = false,
    onLoadMore,
    requireCompleteContextSelection = false,
    onSaveChanges,
  } = props;

  const [selectedBindingRecordUid, setSelectedBindingRecordUid] = useState<string | null>(null);
  const [addedBindingItems, setAddedBindingItems] = useState<IWorkflowBaseRecord[]>([]);
  const [removedRelationItems, setRemovedRelationItems] = useState<IWorkflowBaseRecord[]>([]);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const hasCompleteContextSelection = (items?: IContextSelectorDefinition[], values?: IContextSelectionMap): boolean => {
    if (!items || items.length === 0 || !values) {
      return true;
    }

    return items.every((selector) => Boolean(values[selector.key]));
  };

  const isContextReady = hasCompleteContextSelection(selectors, contextSelection);
  const shouldBlockContent = requireCompleteContextSelection && !isContextReady;

  const getBindingRecordFromRelation = (relationRecord: IWorkflowBaseRecord): IWorkflowBaseRecord | null => {
    if (!node.binding?.key) {
      return null;
    }

    const relationMap = relationRecord as unknown as Record<string, unknown>;
    const nestedValue = relationMap[node.binding.key];

    if (!nestedValue || typeof nestedValue !== "object") {
      return null;
    }

    return nestedValue as IWorkflowBaseRecord;
  };

  const getBindingUidFromRelation = (relationRecord: IWorkflowBaseRecord): string | null => {
    return getBindingRecordFromRelation(relationRecord)?.uid ?? null;
  };

  const buildSyntheticRelationRecord = (bindingItem: IWorkflowBaseRecord): IWorkflowBaseRecord => {
    const syntheticRecord: IWorkflowBaseRecord = {
      uid: `temp-added-${bindingItem.uid}`,
    };

    if (node.binding?.key) {
      syntheticRecord[node.binding.key] = bindingItem;
    }

    selectors?.forEach((selector) => {
      const selectedValue = contextSelection[selector.key];

      if (!selectedValue) {
        return;
      }

      const relationFieldKey = selector.queryParamKey ?? selector.key;
      syntheticRecord[relationFieldKey] = selectedValue;
    });

    return syntheticRecord;
  };

  const removedRelationUidSet = useMemo(() => {
    return new Set(removedRelationItems.map((item) => item.uid));
  }, [removedRelationItems]);

  const effectiveRelationRecords = useMemo(() => {
    const persistedRecords = records.filter((record) => !removedRelationUidSet.has(record.uid));
    const syntheticAddedRecords = addedBindingItems.map((bindingItem) => buildSyntheticRelationRecord(bindingItem));

    return [...persistedRecords, ...syntheticAddedRecords];
  }, [records, removedRelationUidSet, addedBindingItems, node.binding?.key, selectors, contextSelection]);

  const effectiveUsedBindingUidSet = useMemo(() => {
    const usedUidSet = new Set<string>();

    records.forEach((relationRecord) => {
      if (removedRelationUidSet.has(relationRecord.uid)) {
        return;
      }

      const bindingUid = getBindingUidFromRelation(relationRecord);

      if (bindingUid) {
        usedUidSet.add(bindingUid);
      }
    });

    addedBindingItems.forEach((bindingItem) => {
      usedUidSet.add(bindingItem.uid);
    });

    return usedUidSet;
  }, [records, removedRelationUidSet, addedBindingItems]);

  const selectedRelationRecord = useMemo(() => {
    return effectiveRelationRecords.find((record) => record.uid === selectedRecordUid) ?? null;
  }, [effectiveRelationRecords, selectedRecordUid]);

  const selectedDisplayRecord = useMemo(() => {
    if (!selectedRelationRecord) {
      return null;
    }

    if (!node.binding?.key) {
      return selectedRelationRecord;
    }

    const recordMap = selectedRelationRecord as unknown as Record<string, unknown>;
    const nestedValue = recordMap[node.binding.key];

    if (nestedValue && typeof nestedValue === "object") {
      return nestedValue as IWorkflowBaseRecord;
    }

    return selectedRelationRecord;
  }, [selectedRelationRecord, node.binding?.key]);

  const hasPendingChanges = addedBindingItems.length > 0 || removedRelationItems.length > 0;

  const createBulkMutation = usePostMutation<void, IConditionalRelationBulkCreatePayload>({
    mutationOptions: {
      onError: (mutationError) => {
        console.error("Error creating bulk conditional relations", mutationError);
      },
    },
  });

  const deleteBulkMutation = useDeleteWithBodyMutation<void, IConditionalRelationBulkDeletePayload>({
    mutationOptions: {
      onError: (mutationError) => {
        console.error("Error deleting bulk conditional relations", mutationError);
      },
    },
  });

  const handleCancelChanges = () => {
    setAddedBindingItems([]);
    setRemovedRelationItems([]);
    setSelectedBindingRecordUid(null);
  };

  const handleSaveChanges = async () => {
    if (!hasPendingChanges) {
      return;
    }

    const baseUri = node.requestConfig?.uri;

    if (!baseUri) {
      console.error("Missing requestConfig.uri for conditional relation node");
      return;
    }

    const createBulkUri = node.conditionalRelationConfig?.saveConfig?.createBulkUri ?? `${baseUri}/bulk`;
    const deleteBulkUri = node.conditionalRelationConfig?.saveConfig?.deleteBulkUri ?? `${baseUri}/bulk`;

    const areaUid = contextSelection.areaUid;
    const typeOfServiceUid = contextSelection.serviceUid;

    if (!areaUid || !typeOfServiceUid) {
      console.error("Missing required selector values for save");
      return;
    }

    const createItems: IConditionalRelationBulkCreateItem[] = addedBindingItems.map((bindingItem) => ({
      taskUid: bindingItem.uid,
      areaUid,
      typeOfServiceUid,
    }));

    const deleteUids = removedRelationItems.map((relationItem) => relationItem.uid).filter((uid) => !String(uid).startsWith("temp-added-"));

    try {
      setIsSavingChanges(true);

      if (createItems.length > 0) {
        await createBulkMutation.mutateAsync({
          uri: createBulkUri,
          body: { items: createItems },
        });
      }

      if (deleteUids.length > 0) {
        await deleteBulkMutation.mutateAsync({
          uri: deleteBulkUri,
          body: { uids: deleteUids },
        });
      }

      setAddedBindingItems([]);
      setRemovedRelationItems([]);
      setSelectedBindingRecordUid(null);

      if (selectedRecordUid && String(selectedRecordUid).startsWith("temp-added-")) {
        onSelectRecord(null as unknown as string);
      }

      if (onSaveChanges) {
        await onSaveChanges({
          addedItems: createItems.map((item) => ({ uid: item.taskUid }) as IWorkflowBaseRecord),
          removedItems: removedRelationItems,
        });
      }
    } catch (saveError) {
      console.error("Error saving conditional relation changes", saveError);
    } finally {
      setIsSavingChanges(false);
    }
  };

  const moveAvailableToUsed = (bindingItem: IWorkflowBaseRecord) => {
    setAddedBindingItems((current) => {
      if (current.some((item) => item.uid === bindingItem.uid)) {
        return current;
      }

      return [...current, bindingItem];
    });

    setRemovedRelationItems((current) => {
      return current.filter((relationItem) => getBindingUidFromRelation(relationItem) !== bindingItem.uid);
    });
  };

  const moveUsedToAvailable = (relationRecord: IWorkflowBaseRecord) => {
    const bindingUid = getBindingUidFromRelation(relationRecord);

    if (!bindingUid) {
      return;
    }

    const isSyntheticAddedRecord = relationRecord.uid.startsWith("temp-added-");

    if (isSyntheticAddedRecord) {
      setAddedBindingItems((current) => current.filter((item) => item.uid !== bindingUid));
      return;
    }

    setRemovedRelationItems((current) => {
      if (current.some((item) => item.uid === relationRecord.uid)) {
        return current;
      }

      return [...current, relationRecord];
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeData = active.data.current as IDragItemData | undefined;

    if (!activeData) {
      return;
    }

    if (activeData.source === "available" && over.id === "used-list") {
      moveAvailableToUsed(activeData.record);
      return;
    }

    if (activeData.source === "used" && over.id === "available-list") {
      moveUsedToAvailable(activeData.record);
    }
  };

  useEffect(() => {
    setAddedBindingItems([]);
    setRemovedRelationItems([]);
    setSelectedBindingRecordUid(null);
  }, [node.key, contextSelection.serviceUid, contextSelection.areaUid]);

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          px: 2,
          py: 1.5,
          borderRadius: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 320,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ContextSelectorBar selectors={selectors} values={contextSelection} optionsMap={optionsMap} onChange={onContextChange} />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 0.75,
            minWidth: 360,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              px: 1.5,
              py: 1,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              bgcolor: hasPendingChanges ? "warning.50" : "grey.50",
              borderColor: hasPendingChanges ? "warning.200" : "divider",
              boxShadow: "none",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: "text.primary" }}>
              Pending changes
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Added:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                {addedBindingItems.length}
              </Box>
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Removed:{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                {removedRelationItems.length}
              </Box>
            </Typography>

            <BaseModalComponent
              modalKey={`ConditionalRelation-Changes-${node.key}`}
              modalLabel={`Pending changes - ${node.title}`}
              defaultSizes={{ width: 760, height: 620 }}
              elementTrigger={
                <Tooltip title="View modified items">
                  <span>
                    <IconButton
                      size="small"
                      disabled={!hasPendingChanges}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 2,
                      }}
                    >
                      <VisibilityOutlined fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              }
            >
              <ConditionalRelationChangesModal node={node} addedBindingItems={addedBindingItems} removedRelationItems={removedRelationItems} />
            </BaseModalComponent>

            <Stack direction="row" spacing={1} sx={{ ml: { xs: 0, sm: 1 } }}>
              <Button
                variant="contained"
                disabled={!hasPendingChanges || isSavingChanges}
                onClick={handleSaveChanges}
                sx={{
                  minWidth: 88,
                  height: 36,
                  borderRadius: 2,
                  px: 2,
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                {isSavingChanges ? "Saving..." : "Save"}
              </Button>

              <Button
                variant="outlined"
                disabled={!hasPendingChanges || isSavingChanges}
                onClick={handleCancelChanges}
                sx={{
                  minWidth: 88,
                  height: 36,
                  borderRadius: 2,
                  px: 2,
                  fontWeight: 700,
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Box>

      {shouldBlockContent ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 3,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Select all required values to load the related records.
          </Typography>
        </Paper>
      ) : (
        <>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              gap: 2,
              overflow: "hidden",
            }}
          >
            <RelationRecordList
              title={node.title}
              records={effectiveRelationRecords}
              selectedRecordUid={selectedRecordUid}
              onSelectRecord={onSelectRecord}
              loading={loading}
              error={error}
              hasMore={hasMore}
              isFetchingMore={isFetchingMore}
              onLoadMore={onLoadMore}
              fields={node.fields ?? []}
              binding={node.binding}
              width="40%"
              emptyMessage="No related records found for the selected context."
              loadingMessage="Loading related records..."
              droppableId="used-list"
              draggableSource="used"
            />

            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                overflow: "hidden",
              }}
            >
              <BindingCatalogPanel
                node={node}
                selectedRecordUid={selectedBindingRecordUid}
                onSelectRecord={setSelectedBindingRecordUid}
                pageSize={10}
                fields={[]}
                width="100%"
                excludedBindingUids={effectiveUsedBindingUidSet}
                droppableId="available-list"
                draggableSource="available"
              />
            </Box>
          </Box>
        </>
      )}
    </DndContext>
  );
};
